import React, { useEffect, useMemo, useRef, useState } from "react";
import type { ScheduledOrder, ScheduledWindowBucket } from "~/features/scheduled/types";
import { useDeliveryWindows } from "~/shared/contexts/DeliveryWindowsContext";
import { Outcome } from "~/features/orders/constants";
import { useAutoGrow } from "~/shared/hooks/useAutoGrow";
import { formatWindowName, replaceNamedWindowText } from "~/shared/utils/delivery-window";
import {
    WindowChoiceFields,
    getInitialWindowChoice,
    getWindowChoiceWindow,
    isWindowChoiceIncomplete,
    type WindowChoice,
} from "~/features/orders/components/WindowChoiceFields";

// Normalize outcome for edit form: speak_with_agent is not in our enum, treat as answered
function normalizeOutcomeForEdit(outcome: string | undefined): string {
    if (!outcome) return Outcome.Answered;
    if (outcome.toLowerCase() === 'speak_with_agent') return Outcome.Answered;
    return outcome;
}

type Option = { value: string; label: string };

function InlineSelect({
    value,
    options,
    onChange,
    disabled,
}: {
    value: string;
    options: readonly Option[];
    onChange: (next: string) => void;
    disabled?: boolean;
}) {
    const [open, setOpen] = React.useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const listRef = useRef<HTMLUListElement | null>(null);

    const selectedLabel = useMemo(() => {
        return options.find(o => o.value === value)?.label ?? value;
    }, [options, value]);

    useEffect(() => {
        if (!open) return;
        function onDocMouseDown(e: MouseEvent) {
            if (containerRef.current && containerRef.current.contains(e.target as Node)) return;
            setOpen(false);
        }
        function onKeyDown(e: KeyboardEvent) {
            if (e.key === "Escape") setOpen(false);
        }
        document.addEventListener("mousedown", onDocMouseDown, true);
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("mousedown", onDocMouseDown, true);
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [open]);

    useEffect(() => {
        if (!open) return;
        listRef.current?.focus();
    }, [open]);

    return (
        <div ref={containerRef} style={{ position: "relative", width: "100%" }}>
            <button
                ref={buttonRef}
                type="button"
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={open}
                onClick={() => setOpen(v => !v)}
                style={{
                    width: "100%",
                    textAlign: "left",
                    padding: "10px 36px 10px 12px",
                    fontSize: 14,
                    borderRadius: 6,
                    border: "1px solid var(--border)",
                    background: "white",
                    ...(disabled && { opacity: 0.7, cursor: "not-allowed" }),
                }}
            >
                {selectedLabel}
                <span
                    style={{
                        position: "absolute",
                        right: 12,
                        top: "50%",
                        transform: open ? "translateY(-50%) rotate(180deg)" : "translateY(-50%)",
                        pointerEvents: "none",
                        fontSize: 12,
                        color: "var(--text-muted)",
                        transition: "transform 0.15s ease",
                    }}
                    aria-hidden
                >
                    ▼
                </span>
            </button>

            {open && !disabled && (
                <ul
                    ref={listRef}
                    tabIndex={-1}
                    role="listbox"
                    aria-activedescendant={value}
                    style={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        top: "calc(100% + 6px)",
                        margin: 0,
                        padding: 6,
                        listStyle: "none",
                        background: "white",
                        border: "1px solid var(--border)",
                        borderRadius: 8,
                        boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                        maxHeight: 220,
                        overflowY: "auto",
                        zIndex: 2000,
                        outline: "none",
                    }}
                >
                    {options.map((opt) => {
                        const isSelected = opt.value === value;
                        const handleSelect = (e: React.MouseEvent | React.KeyboardEvent) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onChange(opt.value);
                            setTimeout(() => {
                                setOpen(false);
                                buttonRef.current?.focus();
                            }, 0);
                        };
                        const handleKeyDown = (e: React.KeyboardEvent) => {
                            if (e.key === "Enter" || e.key === " ") handleSelect(e);
                        };
                        return (
                            <li
                                key={opt.value}
                                role="option"
                                aria-selected={isSelected}
                                tabIndex={0}
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={handleSelect}
                                onKeyDown={handleKeyDown}
                                style={{
                                    cursor: "pointer",
                                    background: isSelected ? "var(--neutral-gray)" : "transparent",
                                    borderRadius: 6,
                                }}
                            >
                                <span style={{ display: "block", padding: "8px 10px", fontSize: 14 }}>
                                    {opt.label}
                                </span>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}

// Create dropdown options from Outcome constant - Key is shown, Value is submitted
const transferQueueOutcomeOptions = [
    { value: Outcome.Answered, label: 'Answered' },
    { value: Outcome.Rescheduled, label: 'Rescheduled' },
    { value: Outcome.AlternateWindow, label: 'Alternate window' },
    { value: Outcome.ConfirmedWithVoicemail, label: 'Confirmed with Voicemail' },
    { value: Outcome.ConfirmedWithNoAnswer, label: 'Confirmed with No answer' },
    { value: Outcome.ConfirmedThroughTransfer, label: 'Confirmed through Transfer' },
    { value: Outcome.IncorrectPhoneNumber, label: 'Incorrect phone number' },
    { value: Outcome.Cancelled, label: 'Cancelled' },
] as const;

/** Window sent on save; `type` is not set (metadata windows never carried one at runtime). */
export type ScheduledWindowEdit = Omit<ScheduledWindowBucket, 'type'> & { type?: string };

const DEFAULT_TAIL_TOP = 28;
const TAIL_HEIGHT = 24;
const TAIL_INSET = 8;

type ScheduledOrderEditModalProps = Readonly<{
    order: ScheduledOrder;
    onClose: () => void;
    onSave: (updates: {
        outcome: string;
        confirmedDate: string;
        confirmedWindow: ScheduledWindowEdit | null;
        comments: string;
        notes: string;
    }) => void;
    /** Vertical offset (px) from top of modal for the left-pointing tail so it aligns with the selected row. */
    tailTop?: number | null;
}>;

export function ScheduledOrderEditModal({
    order,
    onClose,
    onSave,
    tailTop,
}: ScheduledOrderEditModalProps) {
    const rootRef = useRef<HTMLDivElement>(null);
    const [panelHeight, setPanelHeight] = useState(0);

    useEffect(() => {
        const el = rootRef.current;
        if (!el) return;
        const ro = new ResizeObserver(() => setPanelHeight(el.offsetHeight));
        ro.observe(el);
        setPanelHeight(el.offsetHeight);
        return () => ro.disconnect();
    }, []);

    const rawTop = tailTop != null ? tailTop : DEFAULT_TAIL_TOP;
    const topPx = panelHeight > 0
        ? Math.max(TAIL_INSET, Math.min(panelHeight - TAIL_HEIGHT - TAIL_INSET, rawTop))
        : rawTop;
    const { presets } = useDeliveryWindows();

    const initialWindow = order.confirmedDateAndWindow?.window;

    // Helper function to format date for date input (YYYY-MM-DD)
    const formatDateForInput = (date: string | Date | undefined | null): string => {
        if (!date) return '';
        // If already in YYYY-MM-DD format, return as-is
        if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
            return date;
        }
        // Otherwise, try to format it
        try {
            const dateObj = typeof date === 'string' ? new Date(date) : date;
            if (isNaN(dateObj.getTime())) return '';
            return dateObj.toISOString().split('T')[0];
        } catch {
            return '';
        }
    };

    const todayMin = useMemo(() => {
        const d = new Date();
        const y = d.getFullYear();
        const mo = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${mo}-${day}`;
    }, []);

    const todayMax = useMemo(() => {
        const d = new Date();
        d.setDate(d.getDate() + 2);
        const y = d.getFullYear();
        const mo = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${mo}-${day}`;
    }, []);

    const [outcome, setOutcome] = React.useState<string>(() => normalizeOutcomeForEdit(order.outcome));
    const [confirmedDate, setConfirmedDate] = React.useState(
        formatDateForInput(order.confirmedDateAndWindow?.newDate)
    );
    const dateTypingRef = useRef(false);
    // Confirmed window: a preset, or a custom From/To range. Starts from the order's confirmed window (a matching
    // preset, otherwise its custom range); with no window it defaults to the first preset, as before.
    const [windowChoice, setWindowChoice] = React.useState<WindowChoice | null>(() =>
        getInitialWindowChoice(initialWindow, presets)
    );
    // Presets may arrive after the panel opens
    useEffect(() => {
        if (windowChoice === null && presets.length > 0) setWindowChoice(getInitialWindowChoice(initialWindow, presets));
    }, [windowChoice, presets, initialWindow]);
    const confirmedWindow = getWindowChoiceWindow(windowChoice, presets);
    const defaultComments = formatWindowName(order.confirmedDateAndWindow?.window).slice(0, 80);
    // Old comments may hold a window name ("Evening (5pm - 9pm)"): show its time range instead
    const existingComments = replaceNamedWindowText(order.comments).trim();
    const nonCancelledComments = existingComments ? existingComments.slice(0, 80) : defaultComments;
    // For cancelled: show existing saved comments but never fall back to the window label
    const initialComments = normalizeOutcomeForEdit(order.outcome) === Outcome.Cancelled
        ? existingComments.slice(0, 80)
        : nonCancelledComments;
    const [comments, setComments] = React.useState(initialComments);
    const [notes, setNotes] = React.useState(order.notes ?? '');
    const commentsRef = useAutoGrow(comments);
    const notesRef = useAutoGrow(notes);

    // Window text that comments sync to: the time range
    const confirmedWindowLabel = formatWindowName(confirmedWindow).trim();

    // If comments are empty, default to the confirmed window label.
    // If user changes the confirmed window, only keep syncing comments when the comments
    // are still just the previous window label (i.e. user hasn't typed a real comment).
    // Cancelled outcome: always keep comments blank, no window-label sync.
    const prevWindowLabelRef = useRef<string>(confirmedWindowLabel.slice(0, 80));
    useEffect(() => {
        if (outcome === Outcome.Cancelled) return;
        const nextLabel = confirmedWindowLabel.slice(0, 80);
        const current = (comments ?? '').slice(0, 80).trim();
        const prevLabel = (prevWindowLabelRef.current ?? '').trim();

        if (!current) {
            if (nextLabel) setComments(nextLabel);
        } else if (current === prevLabel) {
            if (nextLabel && current !== nextLabel) setComments(nextLabel);
        }

        // While a custom range is half filled there is no label yet: keep the last one so syncing resumes after
        if (nextLabel) prevWindowLabelRef.current = nextLabel;
    }, [outcome, confirmedWindowLabel, comments]);

    // Same rules as Transfer Queue edit: only "Alternate window" disables Confirmed Date; Window is always enabled.
    // - Answered: both Confirmed Date and Confirmed Window enabled
    // - Rescheduled: both enabled
    // - Alternate window: Confirmed Date disabled, Confirmed Window enabled
    // - All other outcomes: both enabled
    const isConfirmedDateDisabled = outcome === Outcome.AlternateWindow;
    const isConfirmedWindowDisabled = false;

    const commentsLength = comments.replace(/\r\n/g, '\n').length;
    const notesLength = notes.replace(/\r\n/g, '\n').length;
    const commentsOverLimit = commentsLength > 80;
    const notesOverLimit = notesLength > 250;
    const saveDisabled =
        commentsOverLimit ||
        notesOverLimit ||
        (!isConfirmedDateDisabled && !confirmedDate.trim()) ||
        (outcome === Outcome.AlternateWindow && !confirmedWindow) ||
        // A half-filled custom window must not be silently dropped
        isWindowChoiceIncomplete(windowChoice);

    const handleSave = () => {

        // Validation: same pattern as transfer queue (required fields per outcome, block save with alert)
        if (outcome === Outcome.Rescheduled) {
            const dateTrimmed = confirmedDate?.trim() ?? '';
            if (!dateTrimmed) {
                alert('Please select a confirmed date');
                return;
            }
            const parsed = new Date(dateTrimmed);
            if (Number.isNaN(parsed.getTime())) {
                alert('Please enter a valid confirmed date');
                return;
            }
        }
        if (outcome === Outcome.AlternateWindow && !confirmedWindow) {
            alert('Please select a confirmed window');
            return;
        }

        const trimmed = comments.slice(0, 80).trim();
        let commentsTrimmed: string;
        if (outcome === Outcome.Cancelled) {
            commentsTrimmed = '';
        } else {
            commentsTrimmed = trimmed !== '' ? trimmed : confirmedWindowLabel;
        }
        const notesTrimmed = notes.slice(0, 250).trim();

        onSave({
            outcome,
            confirmedDate,
            confirmedWindow,
            comments: outcome === Outcome.Cancelled ? comments.slice(0, 80).trim() : (commentsTrimmed || defaultComments),
            notes: notesTrimmed,
        });
        onClose();
    };

    return (
        <div
            ref={rootRef}
            className="modal-panel-enter"
            style={{
                position: 'relative',
                background: 'white',
                borderRadius: 8,
                border: '1px solid var(--border)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflow: 'visible',
            }}
        >
            {/* Left-pointing tail — outer triangle (border color) */}
            <div
                style={{
                    position: 'absolute',
                    top: topPx,
                    left: -12,
                    width: 0,
                    height: 0,
                    borderTop: '12px solid transparent',
                    borderBottom: '12px solid transparent',
                    borderRight: '12px solid var(--border)',
                    zIndex: 2,
                }}
            />
            {/* Left-pointing tail — inner white fill */}
            <div
                style={{
                    position: 'absolute',
                    top: topPx + 1,
                    left: -10,
                    width: 0,
                    height: 0,
                    borderTop: '11px solid transparent',
                    borderBottom: '11px solid transparent',
                    borderRight: '11px solid white',
                    zIndex: 3,
                }}
            />
            {/* Fixed header with close button — no overlap with scrollbar or content */}
            <div
                style={{
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    padding: '12px 16px 8px',
                    paddingRight: 24,
                    background: 'white',
                    borderBottom: '1px solid var(--border)',
                }}
            >
                <button
                    onClick={onClose}
                    style={{
                        background: 'white',
                        border: '1px solid var(--border)',
                        borderRadius: '50%',
                        width: 28,
                        height: 28,
                        fontSize: 18,
                        cursor: 'pointer',
                        color: 'var(--text-muted)',
                        padding: 0,
                        lineHeight: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                    }}
                    aria-label="Close"
                >
                    ×
                </button>
            </div>

            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                    overflow: 'hidden',
                }}
            >
                <div style={{ padding: 16, display: 'grid', gap: 8, overflow: 'auto', flex: 1, minHeight: 0 }}>
                    {/* Editable fields */}
                    <div style={{ display: 'grid', gap: 4 }}>
                        <span style={{ fontSize: 14, fontWeight: 600 }}>Outcome</span>
                        <InlineSelect
                            value={outcome}
                            options={transferQueueOutcomeOptions}
                            onChange={(next) => {
                                setOutcome(next);
                                if (next === Outcome.Cancelled) setComments('');
                            }}
                        />
                    </div>

                    <div style={{ display: 'grid', gap: 4 }}>
                        <span style={{ fontSize: 14, fontWeight: 600 }}>Confirmed Date</span>
                        <input
                            type="date"
                            value={confirmedDate}
                            onKeyDown={() => { dateTypingRef.current = true; }}
                            onChange={(e) => {
                                const v = e.target.value;
                                if (!v) {
                                    if (!dateTypingRef.current) setConfirmedDate('');
                                    dateTypingRef.current = false;
                                    return;
                                }
                                dateTypingRef.current = false;
                                const parts = v.split('-');
                                let sanitized = v;
                                if (parts.length === 3) {
                                    const month = parts[1] === '00' ? '01' : parts[1];
                                    const day = parts[2] === '00' ? '01' : parts[2];
                                    sanitized = `${parts[0]}-${month}-${day}`;
                                }
                                if (sanitized < todayMin) { setConfirmedDate(todayMin); return; }
                                if (sanitized > todayMax) { setConfirmedDate(todayMax); return; }
                                setConfirmedDate(sanitized);
                            }}
                            onBlur={(e) => {
                                const v = e.target.value;
                                if (!v) return;
                                if (v < todayMin) setConfirmedDate(todayMin);
                                else if (v > todayMax) setConfirmedDate(todayMax);
                            }}
                            disabled={isConfirmedDateDisabled}
                            min={todayMin}
                            max={todayMax}
                            style={{
                                padding: '10px 12px',
                                fontSize: 14,
                                borderRadius: 6,
                                border: '1px solid var(--border)',
                                outline: 'none',
                                ...(isConfirmedDateDisabled && { opacity: 0.7, cursor: 'not-allowed' }),
                            }}
                        />
                    </div>

                    <WindowChoiceFields
                        value={windowChoice}
                        onChange={setWindowChoice}
                        presets={presets}
                        disabled={isConfirmedWindowDisabled}
                        windowLabel="Confirmed Window"
                        labelGap={4}
                    />

                    <label style={{ display: 'grid', gap: 4 }}>
                        <span style={{ fontSize: 14, fontWeight: 600 }}>Comments</span>
                        <textarea
                            ref={commentsRef}
                            value={comments}
                            onChange={(e) => setComments(e.target.value.replace(/\r\n/g, '\n'))}
                            style={{
                                padding: '10px 12px',
                                fontSize: 14,
                                borderRadius: 6,
                                border: commentsOverLimit ? '2px solid #dc2626' : '1px solid var(--border)',
                                outline: 'none',
                                ...(commentsOverLimit && { boxShadow: '0 0 0 2px rgba(220, 38, 38, 0.35)' }),
                                minHeight: 60,
                                resize: 'none',
                                overflow: 'hidden',
                                whiteSpace: 'pre-wrap',
                            }}
                            placeholder="Enter comments..."
                        />
                        <span style={{ fontSize: 12, textAlign: 'right', color: commentsOverLimit ? '#dc2626' : 'var(--text-muted)' }}>
                            {commentsLength} / 80
                        </span>
                    </label>
                    <label style={{ display: 'grid', gap: 4 }}>
                        <span style={{ fontSize: 14, fontWeight: 600 }}>Notes</span>
                        <textarea
                            ref={notesRef}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value.replace(/\r\n/g, '\n'))}
                            style={{
                                padding: '10px 12px',
                                fontSize: 14,
                                borderRadius: 6,
                                border: notesOverLimit ? '2px solid #dc2626' : '1px solid var(--border)',
                                outline: 'none',
                                ...(notesOverLimit && { boxShadow: '0 0 0 2px rgba(220, 38, 38, 0.35)' }),
                                minHeight: 60,
                                resize: 'none',
                                overflow: 'hidden',
                                whiteSpace: 'pre-wrap',
                            }}
                            placeholder="Enter notes..."
                        />
                        <span style={{ fontSize: 12, textAlign: 'right', color: notesOverLimit ? '#dc2626' : 'var(--text-muted)' }}>
                            {notesLength} / 250
                        </span>
                    </label>
                </div>

                <div style={{ flexShrink: 0, padding: 16, borderTop: '1px solid var(--border)', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button
                        className="secondary"
                        onClick={onClose}
                        style={{ padding: '10px 20px' }}
                    >
                        Cancel
                    </button>
                    <button
                        className="primary"
                        onClick={handleSave}
                        disabled={saveDisabled}
                        style={{
                            padding: '10px 20px',
                            ...(saveDisabled && { opacity: 0.6, cursor: 'not-allowed' }),
                        }}
                    >
                        Save
                    </button>
                </div>
            </div>
        </div>
    );
}
